import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import axios from 'axios';
import { FiLoader, FiX } from 'react-icons/fi';
import ConfirmModalB from '../../common/ConfirmModalB'; // Import your ConfirmModal
import { FaPlus, FaTrash } from 'react-icons/fa';

interface Answer {
  id?: number; 
  _id?: number; 
  name: string; 
  order?: number; 
}

interface Question {
 
  _id?: number; 
  name: string; 
  order?: number; 
  answers: Answer[]; 
}

interface EditCategoryProps {
  categoryId?: number;
  setActiveView: React.Dispatch<React.SetStateAction<'list' | 'add' | 'edit'>>;
}

const NestedAnswers: React.FC<{ control: any; qIndex: number; register: any; questionFields: any }> = ({ control, qIndex, register, questionFields }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `questions.${qIndex}.answers`,
  });

  const handleDeleteAnswer = async (aIndex: number) => {
    const questionData = questionFields[qIndex]; 
    const answerData = fields[aIndex]; 

    const questionId = questionData._id; 
    // @ts-ignore
    const answerId = answerData._id || answerData.id; 

    if (questionId && answerId) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/question/${questionId}/answer/${answerId}`);
      } catch (error) {
        console.error('Errore durante la cancellazione della risposta', error);
      }
    }

    remove(aIndex); // Supprimer la réponse du formulaire localement
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Risposte</span>
        <button
          type="button"
          onClick={() => append({ name: '', order: 0 })} // Initialiser avec des valeurs par défaut
          className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg text-sm hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2"
        >
          <FaPlus /> Aggiungi una risposta
        </button>
      </div>

      <div className="mt-3 space-y-6">
        {fields.map((answer, aIndex) => (
          <div key={answer.id} className="flex gap-4 items-center">
            <input
              {...register(`questions.${qIndex}.answers.${aIndex}.name`, { required: true })}
               // @ts-ignore
              disabled={!!answer._id}
              placeholder="Nome della risposta"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
             
              required
            />
            <input
              {...register(`questions.${qIndex}.answers.${aIndex}.order`)}
              // @ts-ignore
              disabled={!!answer._id}
              type="number"
              placeholder="Ordine"
              className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
            <button
              type="button"
              onClick={() => handleDeleteAnswer(aIndex)}
              className="text-red-500 hover:text-red-700 transition-all"
            >
              <FaTrash />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const AddCategory: React.FC<EditCategoryProps> = ({ categoryId, setActiveView }) => {
  const { register, handleSubmit, control, reset } = useForm();
  const { fields: questionFields, append: appendQuestion, remove: removeQuestion } = useFieldArray({
    control,
    name: 'questions',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Control modal state
  const [confirmationData, setConfirmationData] = useState<any>(null); // Store the confirmation data

  useEffect(() => {
    const fetchCategoryData = async () => {
      if (categoryId) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories/${categoryId}`);
          const data = response.data;
          reset({
            name: data.name || '',
            order: data.order || '',
            icon_type: data.icon_type || '',
            questions: data.questions?.map((question: any) => ({
              _id: question.id, // Assurez-vous que l'ID est correctement récupéré ici
              name: question.text || question.name || '',
              order: question.order || 0,
              answers: question.answers?.map((answer: any) => ({
                _id: answer.id, // Assurez-vous que l'ID est correctement récupéré ici
                name: answer.testo || '',
                order: answer.data_creazione || 0,
              })) || [],
            })) || [],
          });
        } catch (error) {
          console.error('Errore durante il caricamento dei dati:', error);
          setMessage({ type: 'error', text: 'Errore durante il caricamento dei dati' });
        }
      }
    };
  
    fetchCategoryData();
  }, [categoryId, reset]);
  
  const onSubmit = async (data: any) => {
    const newCategoryData = {
        name: data.name,
        order: data.order,
        // On structure les questions et réponses directement dans les données de la catégorie
        questions: data.questions.map((question: any) => ({
            id: question._id, // Utiliser _id qui contient l'ID de la question
            text: question.name,
            order: question.order,
            answers: question.answers.map((answer: any) => ({
                id: answer._id, // Utiliser _id qui contient l'ID de la réponse
                testo: answer.name,
                order: answer.order,
                function: answer.function,
            })),
        })),
    };

    // Afficher les données validées avant envoi
    // console.log("Données validées avant envoi : ", newCategoryData);

    try {
        setIsSubmitting(true);

        if (categoryId) {
            
            // console.log("Modification de la catégorie avec ID:", categoryId);

            // Mettre à jour la catégorie avec son ID
            const updateCategoryResponse = await axios.put(
                `${process.env.REACT_APP_API_URL}/categories/${categoryId}`,
                newCategoryData
            );

            setMessage({
                type: 'success',
                text: 'La categoria e le sue domande sono state modificate con successo.',
            });
            setTimeout(() => setActiveView('list'), 2000);

        } else {
            // Cas d'ajout d'une nouvelle catégorie : on vérifie si la catégorie existe déjà
            const existingCategoryResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/categories?name=${data.name}`
            );

            console.log('Risposta di verifica della categoria', existingCategoryResponse.data);

            if (existingCategoryResponse.data.length > 0) {
                const existingCategoryId = existingCategoryResponse.data[0].id;
                console.log('ID della categoria esistente:', existingCategoryId);

                setConfirmationData({
                    title: "Categoria esistente",
                    message: "Esiste già una categoria con questo nome. Vuoi aggiornare le informazioni di questa categoria con i nuovi dettagli?",
                    onConfirm: async () => {
                        await axios.put(
                            `${process.env.REACT_APP_API_URL}/categories/${existingCategoryId}`,
                            newCategoryData
                        );

                        setMessage({
                            type: 'success',
                            text: 'La categoria e le sue domande sono state aggiornate con successo.',
                        });
                        setIsModalOpen(false);
                        setTimeout(() => setActiveView('list'), 2000);
                    },
                    onCancel: () => {
                        setIsModalOpen(false);
                    }
                });
                setIsModalOpen(true);
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/categories`, newCategoryData);
                setMessage({
                    type: 'success',
                    text: 'La categoria e le sue domande sono state aggiunte con successo.',
                });
                setTimeout(() => setActiveView('list'), 2000);
            }
        }
    } catch (error: any) {
        console.error('Errore durante il salvataggio della categoria:', error);
        setMessage({
            type: 'error',
            text: error.response?.data?.error || error.message || 'Errore durante il salvataggio',
        });
    } finally {
        setIsSubmitting(false);
    }
};

  const handleRemoveQuestion = async (qIndex: number) => {
    const questionData = questionFields[qIndex];
    // @ts-ignore  
    const questionId = questionData._id;

    if (questionId) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/questions/${questionId}/remove`);
      } catch (error) {
        console.error('Errore durante la cancellazione della domanda', error);
        setMessage({ type: 'error', text: 'Errore durante la cancellazione della domanda' });
      }
    }
    removeQuestion(qIndex);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 transition-all duration-300 hover:shadow-md">
      <div className="px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-gray-100/50">
        <h2 className="font-semibold text-gray-700">{categoryId ? 'Modifica una categoria' : 'Aggiungi una categoria'}</h2>
      </div>

      <div className="p-5">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {message && (
            <div
              className={`text-sm p-3 rounded ${message.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-100 text-green-700'} flex justify-between items-center`}
            >
              {message.text}
              <button onClick={() => setMessage(null)} className="ml-2">
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Form fields here */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome della categoria</label>
            <input
              {...register('name')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
              <input
                type="number"
                {...register('order')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icona</label>
              <input
                {...register('icon_type')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Domande</span>
              <button
                type="button"
                onClick={() => appendQuestion({ name: '', answers: [] })}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg text-sm hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2"
              >
                <FaPlus /> Aggiungi una domanda
              </button>
            </div>

            <div className="mt-3 space-y-6">
              {questionFields.map((question, qIndex) => (
                <div key={question.id} className="border p-4 rounded-lg shadow-sm bg-gray-50">
                  <div className="flex gap-4 items-center">
                    <input
                      {...register(`questions.${qIndex}.name`, { required: true })}
                      // @ts-ignore
                      disabled={!!question._id}
                      placeholder="Nome della domanda"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      required
                    />
                    <input
                      {...register(`questions.${qIndex}.order`)}
                      
                      type="number"
                      placeholder="Ordine"
                      className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveQuestion(qIndex)}
                      className="text-red-500 hover:text-red-700 transition-all"
                    >
                      <FaTrash />
                    </button>
                  </div>
                  <NestedAnswers control={control} qIndex={qIndex} register={register} questionFields={questionFields} />
                </div>
              ))}
            </div>
          </div>


          <div className="mt-5">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-lg text-white font-medium text-lg focus:outline-none ${
                isSubmitting
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 transition-all'
              }`}
            >
              {isSubmitting ? <FiLoader className="animate-spin inline-block" /> : 'Salva categoria'}
            </button>
          </div>
        </form>
      </div>

      {/* Confirm Modal */}
      {isModalOpen && confirmationData && (
        <ConfirmModalB
          isOpen={isModalOpen}
          title={confirmationData.title}
          message={confirmationData.message}
          onConfirm={confirmationData.onConfirm}
          onCancel={confirmationData.onCancel}
        />
      )}
    </div>
  );
};

export default AddCategory;
